import React, { useMemo, useState } from 'react';
import { useOpenLayersContext } from '@ais-ui/react-openlayers';
import { fromLonLat } from 'ol/proj';
import {
  Button,
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { PiBookBookmarkThin } from 'react-icons/pi';
import { EmptyCommand } from '../../../../components/EmptyCommand';
import { ROUTES } from '../../../../routes';
import { useGetBookmarks } from '@ais-ui/websockets';

export const Bookmarks = () => {
  const { data, isLoading, isError } = useGetBookmarks();

  const bookmarks = useMemo(() => {
    if (data) {
      return data.sort((a, b) => a.name.localeCompare(b.name));
    }
    return [];
  }, [data]);

  const [open, setOpen] = useState(false);
  const { map } = useOpenLayersContext();

  const onBookmarkSelect = (bookmarkName: string) => {
    const bookmark = bookmarks.find(
      (b) => b.name.toLowerCase() === bookmarkName.toLowerCase()
    );

    if (bookmark) {
      const view = map.getView();

      // Set the center and zoom on the view
      view.setCenter(fromLonLat([bookmark.lon, bookmark.lat]));
      view.setZoom(bookmark.zoomLevel);
    }

    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              id="bookmarks-button"
              size="icon"
              variant="outline"
              className="h-10 w-10 flex-shrink-0 flex-grow-0"
            >
              <PiBookBookmarkThin className="size-5"></PiBookBookmarkThin>
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>Go to bookmark location</TooltipContent>
      </Tooltip>
      <PopoverContent>
        {bookmarks.length === 0 && (
          <EmptyCommand
            entity="bookmark"
            srcImage="empty_list_bro.png"
            to={ROUTES.SETTINGS.BOOKMARKS.$path()}
          ></EmptyCommand>
        )}
        {bookmarks.length > 0 && (
          <Command>
            <CommandInput placeholder="Find bookmark..." />
            <CommandList>
              <ScrollArea className="flex-grow overflow-y-auto pr-4 h-60">
                <CommandGroup heading="Zoom to bookmark">
                  {bookmarks.map((bookmark) => (
                    <CommandItem
                      key={bookmark.uuid}
                      value={bookmark.name}
                      onSelect={onBookmarkSelect}
                    >
                      {bookmark.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </ScrollArea>
            </CommandList>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  );
};
